<template>
    <vx-modal v-model:visible="visible" size="lg" padding="standard">
        <template v-slot:header>Edit brand</template>
        <template v-slot:subheader>{{ props.brandId }}</template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="props.identityLocked" color="info" :closeable="false" data-test="identity-locked">
                Legal company name, EIN, EIN issuing country and entity type cannot change while the brand has a vet or
                an active campaign.
            </vx-alert>
            <vx-alert v-else-if="verified" color="info" :closeable="false" data-test="identity-reset">
                Changing the legal company name, EIN, EIN issuing country or entity type makes the brand unverified
                until you Verify Identity.
            </vx-alert>

            <kiosk-tcr-brand-form-details
                v-model="form"
                :enums="props.enums"
                :externalResults="$externalResults"
                :identityLocked="props.identityLocked"
                editing
            />
            <kiosk-tcr-brand-form-address v-model="form" :externalResults="$externalResults" />
            <kiosk-tcr-brand-form-advanced
                v-model="form"
                :enums="props.enums"
                :externalResults="$externalResults"
                :identityLocked="props.identityLocked"
            />
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    data-test="cancel-brand"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="save()"
                    data-test="save-brand"
                    :loading="saving"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Save
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxAlert, VxButton, VxModal } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { computed, ref, watch } from 'vue';
import KioskTcrBrandFormAddress from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandFormAddress.vue';
import KioskTcrBrandFormAdvanced from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandFormAdvanced.vue';
import KioskTcrBrandFormDetails from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandFormDetails.vue';
import { brandUpdateKeys } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationFormDefaults';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type {
    TcrBrandCreate,
    TcrBrandUpdate,
    TcrEnums,
    TcrLiveBrand,
} from '~/services/adminTenDlcRegistrationClient.types';
import { e164Format } from '~/utils/phoneNumber';
import { validateRules } from '~/utils/validation';

const props = defineProps<{
    brandId: string;
    brand: TcrLiveBrand;
    enums: TcrEnums | null;
    identityLocked: boolean;
}>();
const emit = defineEmits<{ updated: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { $externalResults, catchError } = useFormRequestHandler();
const toasted = useToasted();

// The live record is nullable field by field; vx-input and vx-selectable never receive null, and TCR's NONE
// alternate id type is the picker's blank.
const seedForm = (brand: TcrLiveBrand): TcrBrandCreate => ({
    ...(Object.fromEntries(brandUpdateKeys.map((key) => [key, brand[key] ?? ''])) as TcrBrandUpdate),
    alt_business_id_type: 'NONE' === brand.alt_business_id_type ? '' : brand.alt_business_id_type ?? '',
    customer_tag: '',
});

const toPayload = (form: TcrBrandCreate): TcrBrandUpdate => ({
    ...(Object.fromEntries(brandUpdateKeys.map((key) => [key, form[key]])) as TcrBrandUpdate),
    phone: e164Format(form.phone) ?? form.phone,
    // A blank would keep the old value; NONE clears it.
    alt_business_id_type: form.alt_business_id_type || 'NONE',
});

const form = ref<TcrBrandCreate>(seedForm(props.brand));
// Owns the external results so validateRules() clears them for every section before a resubmit.
const validate = useVuelidate({ $externalResults });
const saving = ref(false);

const verified = computed(() => ['VERIFIED', 'VETTED_VERIFIED'].includes(props.brand.identity_status ?? ''));

watch(visible, (open) => {
    if (open) {
        form.value = seedForm(props.brand);
        $externalResults.value = {};
        validate.value.$reset();
    }
});

const save = async () => {
    if (saving.value) {
        return;
    }

    saving.value = true;
    validate.value.$reset();

    if (!(await validateRules(validate))) {
        saving.value = false;
        return;
    }

    try {
        await adminTenDlcRegistrationClient.updateBrand(props.brandId, toPayload(form.value));

        toasted.success('Brand updated.');
        emit('updated');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        saving.value = false;
    }
};
</script>
